import React, { useState, useEffect } from 'react';
import { useJarvisContext } from '../../context/JarvisContext';
import {
  getNotifPrefs, saveNotifPrefs,
  getPriceAlerts, savePriceAlert, deletePriceAlert,
  requestPermission, getPermissionStatus,
} from '../../services/notificationService';
import { exportAllData, getExportJSON, importAllData, clearAllData } from '../../services/storage';
import { forceRefreshApp } from '../../serviceWorkerRegistration';
import { getTrackerPrefs, saveTrackerPrefs, startKeepAlive, stopKeepAlive } from '../../services/positionTrackerService';

const THEME = {
  colors: {
    bg: '#0a0e1a', card: '#111827', border: '#1e293b',
    text: '#e2e8f0', textMuted: '#94a3b8',
    green: '#00ff88', red: '#ff3333', gold: '#ffd700',
    blue: '#00d4ff', purple: '#a78bfa', orange: '#fb923c',
  },
};

const S = {
  page:    { padding: '16px', color: THEME.colors.text, fontFamily: 'monospace', maxWidth: '100%' },
  title:   { fontSize: '18px', fontWeight: '700', color: THEME.colors.gold, marginBottom: '4px', letterSpacing: '2px' },
  sub:     { fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '16px', letterSpacing: '1px' },
  card:    (bg) => ({ background: bg || THEME.colors.card, border: `1px solid ${THEME.colors.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' }),
  section: { fontSize: '10px', fontWeight: '700', color: THEME.colors.textMuted, letterSpacing: '2px', marginBottom: '10px' },
  label:   { fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px' },
  input:   { width: '100%', background: '#0d1117', border: `1px solid ${THEME.colors.border}`, borderRadius: '6px', padding: '8px 10px', color: THEME.colors.text, fontSize: '13px', fontFamily: 'monospace', boxSizing: 'border-box' },
  btn:     (c) => ({ background: c === 'green' ? 'rgba(0,255,136,0.15)' : c === 'red' ? 'rgba(255,51,51,0.15)' : c === 'primary' ? 'rgba(0,212,255,0.15)' : c === 'gold' ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${c === 'green' ? THEME.colors.green : c === 'red' ? THEME.colors.red : c === 'primary' ? THEME.colors.blue : c === 'gold' ? THEME.colors.gold : THEME.colors.border}`, borderRadius: '6px', padding: '8px 14px', color: c === 'green' ? THEME.colors.green : c === 'red' ? THEME.colors.red : c === 'primary' ? THEME.colors.blue : c === 'gold' ? THEME.colors.gold : THEME.colors.text, fontSize: '12px', cursor: 'pointer', fontFamily: 'monospace', fontWeight: '600' }),
  tabBtn:  (a) => ({ background: a ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)', border: `1px solid ${a ? THEME.colors.blue : THEME.colors.border}`, borderRadius: '6px', padding: '7px 6px', color: a ? THEME.colors.blue : THEME.colors.textMuted, fontSize: '11px', cursor: 'pointer', fontFamily: 'monospace', flex: 1 }),
  row:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
};

function Toggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)}
      style={{ width: '40px', height: '22px', borderRadius: '11px', background: value ? THEME.colors.green : 'rgba(255,255,255,0.1)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: '3px', left: value ? '20px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: value ? '#000' : THEME.colors.textMuted, transition: 'left 0.2s' }} />
    </div>
  );
}

const IS_IOS_UA = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// ─── Microphone permission card ─────────────────────────────────────────────
// The floating EnableMicrophone.jsx banner has real conditions that can hide
// it (chat screen, drawer open, session-dismissed) — this is a guaranteed,
// always-reachable alternative that isn't subject to any of that, for when
// the banner alone isn't getting through.
function MicPermissionCard() {
  const [status, setStatus] = useState('unknown'); // 'unknown' | 'prompt' | 'granted' | 'denied'

  useEffect(() => {
    let permStatus;
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'microphone' })
        .then((s) => { permStatus = s; setStatus(s.state); s.onchange = () => setStatus(s.state); })
        .catch(() => setStatus('prompt')); // browser can't query this permission name — fall back to showing the ask
    } else {
      setStatus('prompt');
    }
    return () => { if (permStatus) permStatus.onchange = null; };
  }, []);

  const handleEnable = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop()); // only needed it to trigger the real permission prompt
      setStatus('granted');
    } catch {
      setStatus('denied');
    }
  };

  const micColor = status === 'granted' ? THEME.colors.green : status === 'denied' ? THEME.colors.red : THEME.colors.gold;
  const deniedHelp = IS_IOS_UA
    ? 'Settings app → Safari → Microphone मध्ये जाऊन allow करा.'
    : 'पत्त्याच्या डाव्या बाजूला 🔒 आयकॉनवर टॅप करा → Permissions → Microphone → Allow, मग page reload करा.';

  return (
    <div style={{ ...S.card(`rgba(${status === 'granted' ? '0,255,136' : status === 'denied' ? '255,51,51' : '255,215,0'},0.06)`), padding: '12px 14px', marginBottom: '12px' }}>
      <div style={S.row}>
        <div style={{ flex: 1, marginRight: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: micColor }}>
            {status === 'granted' ? '🎙️ Microphone Enabled' : status === 'denied' ? '🎙️ Microphone Blocked' : '🎙️ Microphone Permission Not Granted'}
          </div>
          <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '3px' }}>
            {status === 'granted' ? 'बोलून JARVIS ला विचारता येईल' : status === 'denied' ? deniedHelp : 'खालच्या बटणावर टॅप करून mic access द्या — यानंतर बोलून विचारता येईल'}
          </div>
        </div>
        {status !== 'granted' && status !== 'denied' && (
          <button style={S.btn('gold')} onClick={handleEnable}>Enable</button>
        )}
      </div>
    </div>
  );
}

// ─── Notifications Tab ───────────────────────────────────────────────────────
function NotificationsTab() {
  const [prefs, setPrefs]       = useState(getNotifPrefs());
  const [permission, setPerm]   = useState(getPermissionStatus());
  const [saved, setSaved]       = useState(false);
  const [trackerOn, setTrackerOn] = useState(() => getTrackerPrefs().enabled);

  const updatePref = (key, val) => setPrefs(p => ({ ...p, [key]: val }));

  // Called directly from the toggle's own click, not just on Save — this
  // click IS the user gesture the Media Session audio keep-alive needs to
  // actually start playing (autoplay policy blocks it without one).
  const handleToggleTracker = (val) => {
    setTrackerOn(val);
    saveTrackerPrefs({ enabled: val });
    if (val) startKeepAlive(); else stopKeepAlive();
  };

  const handleEnable = async () => {
    const result = await requestPermission();
    setPerm(result);
    if (result === 'granted') {
      updatePref('enabled', true);
    }
  };

  const handleSave = () => {
    saveNotifPrefs(prefs);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const permColor = permission === 'granted' ? THEME.colors.green : permission === 'denied' ? THEME.colors.red : THEME.colors.gold;

  return (
    <>
      <MicPermissionCard />
      {/* Permission status */}
      <div style={{ ...S.card(`rgba(${permission === 'granted' ? '0,255,136' : permission === 'denied' ? '255,51,51' : '255,215,0'},0.06)`), padding: '12px 14px' }}>
        <div style={S.row}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: permColor }}>
              {permission === 'granted' ? 'Notifications Enabled' : permission === 'denied' ? 'Notifications Blocked' : 'Permission Not Granted'}
            </div>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '3px' }}>
              {permission === 'granted' ? 'Browser will show JARVIS alerts' : permission === 'denied' ? 'Enable in browser settings (site permissions)' : 'Click to enable browser notifications'}
            </div>
          </div>
          {permission !== 'granted' && permission !== 'denied' && (
            <button style={S.btn('gold')} onClick={handleEnable}>Enable</button>
          )}
        </div>
      </div>

      {/* Toggle settings */}
      <div style={S.card()}>
        <div style={S.section}>NOTIFICATION SETTINGS</div>

        {[
          { key: 'sessionAlerts', label: 'Session Start Alerts', desc: 'Pre-London (8:30 IST), London (9:00), NY (14:00)' },
          { key: 'recapReminder', label: 'Daily Recap Reminder', desc: 'Evening reminder to log your daily lesson (8 PM IST)' },
          { key: 'riskAlerts',    label: 'Risk Limit Alerts',    desc: 'Notify when approaching daily loss limit' },
          { key: 'priceAlerts',   label: 'Price Level Alerts',  desc: 'Custom price alerts (set in Price Alerts tab)' },
        ].map(item => (
          <div key={item.key} style={{ ...S.row, marginBottom: '14px' }}>
            <div style={{ flex: 1, marginRight: '12px' }}>
              <div style={{ fontSize: '13px' }}>{item.label}</div>
              <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{item.desc}</div>
            </div>
            <Toggle value={prefs[item.key] || false} onChange={val => updatePref(item.key, val)} />
          </div>
        ))}

        <button style={{ ...S.btn(saved ? 'green' : 'primary'), width: '100%', padding: '10px' }} onClick={handleSave}>
          {saved ? 'Saved!' : 'Save Notification Settings'}
        </button>
      </div>

      {/* Position Tracker */}
      <div style={S.card(trackerOn ? 'rgba(0,212,255,0.06)' : undefined)}>
        <div style={S.section}>POSITION TRACKER</div>
        <div style={{ ...S.row, marginBottom: '10px' }}>
          <div style={{ flex: 1, marginRight: '12px' }}>
            <div style={{ fontSize: '13px' }}>Speak open trades every 5 min</div>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>
              JARVIS ने detect केलेले आणि तुम्ही स्वतः टाकलेले — दोन्ही open trades साठी price/profit update बोलून सांगेल.
            </div>
          </div>
          <Toggle value={trackerOn} onChange={handleToggleTracker} />
        </div>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, lineHeight: '1.5' }}>
          ⚠️ App उघडी असेल तेव्हाच काम करतं. Screen चालू असताना सगळ्यात विश्वासार्ह — screen लॉक
          असताना Android कधी कधी थांबवू शकतो, तरी हे शक्य तितकं विश्वासार्ह ठेवण्यासाठी background audio
          session वापरलंय.
        </div>
      </div>

      {/* IST Session reference */}
      <div style={{ ...S.card('rgba(0,212,255,0.04)'), padding: '12px 14px' }}>
        <div style={S.section}>SESSION TIMES (IST)</div>
        {[
          { time: '08:30', label: 'Pre-London Setup', color: THEME.colors.gold },
          { time: '09:00', label: 'London Opens',     color: THEME.colors.green },
          { time: '14:00', label: 'NY Opens (Prime)',  color: THEME.colors.green },
          { time: '20:00', label: 'Daily Recap',       color: THEME.colors.blue  },
        ].map(s => (
          <div key={s.time} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
            <span style={{ color: THEME.colors.textMuted }}>{s.time} IST</span>
            <span style={{ color: s.color }}>{s.label}</span>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Price Alerts Tab ─────────────────────────────────────────────────────────
function PriceAlertsTab() {
  const { state } = useJarvisContext();
  const livePrice = state.livePrice || 0;
  const [alerts, setAlerts] = useState(getPriceAlerts());
  const [form, setForm]     = useState({ price: '', direction: 'both', label: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAdd = () => {
    if (!form.price) return;
    savePriceAlert(form);
    setAlerts(getPriceAlerts());
    setForm({ price: '', direction: 'both', label: '' });
  };

  const handleDelete = (id) => {
    deletePriceAlert(id);
    setAlerts(getPriceAlerts());
  };

  const activeAlerts = alerts.filter(a => a.active);
  const pastAlerts   = alerts.filter(a => !a.active);

  return (
    <>
      {livePrice > 0 && (
        <div style={{ ...S.card('rgba(0,212,255,0.06)'), padding: '10px 14px' }}>
          <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>Live XAU/USD: </span>
          <span style={{ fontSize: '14px', fontWeight: '700', color: THEME.colors.green }}>${livePrice.toFixed(2)}</span>
        </div>
      )}

      <div style={S.card()}>
        <div style={S.section}>ADD PRICE ALERT</div>
        <div style={{ marginBottom: '10px' }}>
          <div style={S.label}>Price Level ($)</div>
          <input style={S.input} type="number" step="0.01" placeholder="e.g. 3500.00" value={form.price} onChange={e => set('price', e.target.value)} />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <div style={S.label}>Direction</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[{ id: 'above', label: 'Above' }, { id: 'below', label: 'Below' }, { id: 'both', label: 'Both' }].map(d => (
              <button key={d.id} style={{ ...S.btn(form.direction === d.id ? 'primary' : ''), flex: 1, fontSize: '11px', padding: '6px 4px' }}
                onClick={() => set('direction', d.id)}>
                {d.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <div style={S.label}>Label (optional)</div>
          <input style={S.input} placeholder="e.g. Sir's 0.75 BUY zone, Daily OB, etc." value={form.label} onChange={e => set('label', e.target.value)} />
        </div>
        <button style={{ ...S.btn('green'), width: '100%', padding: '10px' }} onClick={handleAdd}>
          + Add Alert
        </button>
      </div>

      {activeAlerts.length > 0 && (
        <div style={S.card()}>
          <div style={S.section}>ACTIVE ALERTS ({activeAlerts.length})</div>
          {activeAlerts.map(a => (
            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '8px', background: 'rgba(0,212,255,0.04)', borderRadius: '6px' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: THEME.colors.gold }}>${a.price.toFixed(2)}</div>
                <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>
                  {a.direction === 'above' ? ' When price goes above' : a.direction === 'below' ? ' When price goes below' : ' Either direction'}
                  {a.label ? `  ${a.label}` : ''}
                </div>
              </div>
              <button style={{ ...S.btn('red'), padding: '4px 10px', fontSize: '11px' }} onClick={() => handleDelete(a.id)}>X</button>
            </div>
          ))}
        </div>
      )}

      {pastAlerts.length > 0 && (
        <div style={S.card()}>
          <div style={S.section}>TRIGGERED ({pastAlerts.length})</div>
          {pastAlerts.slice(-5).map(a => (
            <div key={a.id} style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '4px' }}>
              [HIT] {a.price} {a.label ? `(${a.label})` : ''}
            </div>
          ))}
        </div>
      )}

      {activeAlerts.length === 0 && pastAlerts.length === 0 && (
        <div style={{ textAlign: 'center', color: THEME.colors.textMuted, fontSize: '13px', padding: '20px' }}>
          No alerts set. Add a price level to be notified when XAU/USD reaches it.
        </div>
      )}
    </>
  );
}

// ─── Data Tab ─────────────────────────────────────────────────────────────────
function DataTab() {
  const [msg, setMsg] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const handleForceRefresh = async () => {
    setRefreshing(true);
    // Always navigates away at the end (see forceRefreshApp) — the page
    // reloads before this ever gets a chance to set refreshing back to
    // false, which is fine, that's the point.
    await forceRefreshApp();
  };

  const handleExport = async () => {
    try {
      if (window.electronAPI && window.electronAPI.isElectron) {
        const { json, filename } = getExportJSON();
        const result = await window.electronAPI.saveFile(filename, json);
        if (result.success) {
          setMsg('Backup saved: ' + result.filePath);
        }
      } else {
        exportAllData();
        setMsg('Data exported successfully');
      }
    } catch (e) {
      setMsg('Export failed: ' + e.message);
    }
    setTimeout(() => setMsg(''), 4000);
  };
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        importAllData(JSON.stringify(data)); // importAllData expects a JSON string
        setMsg('Data imported - refresh to see changes');
      } catch {
        setMsg('Invalid backup file');
      }
      setTimeout(() => setMsg(''), 4000);
    };
    reader.readAsText(file);
  };

  const handleClear = () => {
    if (!window.confirm('Delete ALL JARVIS data? This cannot be undone.')) return;
    clearAllData();
    setMsg('All data cleared - refresh app');
    setTimeout(() => setMsg(''), 3000);
  };

  return (
    <>
      <div style={{ ...S.card('rgba(0,212,255,0.05)'), padding: '14px' }}>
        <div style={S.section}>APP UPDATE</div>
        <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '12px' }}>
          JARVIS ला offline पण चालू ठेवण्यासाठी नवीन version cache करून ठेवतो — म्हणजे कधी कधी update दिसायला थोडा वेळ लागतो.
          खासकरून installed app मध्ये (browser refresh बटण नसताना) हे इथून करा. यामुळे फक्त app चा cached code साफ होतो —
          तुमचे trades/rules/journal काहीही डिलीट होत नाही.
        </div>
        <button style={{ ...S.btn('primary'), width: '100%', padding: '10px' }} onClick={handleForceRefresh} disabled={refreshing}>
          {refreshing ? '🔄 Refreshing…' : '🔄 Refresh App (Clear Cache)'}
        </button>
      </div>

      <div style={S.card()}>
        <div style={S.section}>BACKUP & RESTORE</div>

        <button style={{ ...S.btn('primary'), width: '100%', padding: '11px', marginBottom: '10px' }} onClick={handleExport}>
          Export All JARVIS Data (JSON)
        </button>

        <div style={{ marginBottom: '10px' }}>
          <div style={S.label}>Import from backup</div>
          <input type="file" accept=".json"
            style={{ ...S.input, padding: '6px 10px', cursor: 'pointer' }}
            onChange={handleImport} />
        </div>

        {msg && <div style={{ fontSize: '12px', color: msg.startsWith('Data') ? THEME.colors.green : THEME.colors.red, marginTop: '8px' }}>{msg}</div>}
      </div>

      <div style={{ ...S.card('rgba(255,51,51,0.05)'), padding: '14px' }}>
        <div style={S.section}>DANGER ZONE</div>
        <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '12px' }}>
          Deletes all trades, rules, knowledge, ICT data, psychology logs, and JARVIS memory. Cannot be undone. Export first.
        </div>
        <button style={{ ...S.btn('red'), width: '100%', padding: '10px' }} onClick={handleClear}>
          Clear All Data
        </button>
      </div>
    </>
  );
}

// ─── AI Keys Tab ──────────────────────────────────────────────────────────────
function AIKeysTab() {
  const [claudeKey, setClaudeKey] = useState('');
  const [groqKey,   setGroqKey]   = useState('');
  const [status,    setStatus]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [engine,    setEngine]    = useState(null);
  const [prefSaving, setPrefSaving] = useState(false);

  const refreshStatus = () =>
    fetch('/api/config/status').then(r => r.json()).then(d => setEngine(d)).catch(() => {});

  React.useEffect(() => { refreshStatus(); }, []);

  const saveKey = async (provider, key) => {
    setLoading(true);
    setStatus(null);
    try {
      const r = await fetch('/api/config/apikey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: key, provider }),
      });
      const d = await r.json();
      if (d.success) {
        setStatus({ ok: true, msg: `${provider === 'claude' ? 'Claude' : 'Groq'} key saved!` });
        await refreshStatus();
      } else {
        setStatus({ ok: false, msg: d.error || 'Save failed' });
      }
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    }
    setLoading(false);
  };

  const setPreference = async (pref) => {
    setPrefSaving(true);
    try {
      const r = await fetch('/api/config/provider', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preference: pref }),
      });
      const d = await r.json();
      if (d.success) await refreshStatus();
    } catch {}
    setPrefSaving(false);
  };

  const engineColor = engine?.activeEngine === 'claude' ? THEME.colors.blue
    : engine?.activeEngine === 'groq' ? THEME.colors.green : THEME.colors.red;
  const engineLabel = engine?.activeEngine === 'claude' ? 'Claude AI — ACTIVE'
    : engine?.activeEngine === 'groq' ? 'Groq — ACTIVE' : 'AI OFF / No Key';

  const pref = engine?.preference || 'auto';
  const prefOptions = [
    { id: 'auto',   label: 'AUTO',   desc: 'Claude first, Groq fallback',  color: THEME.colors.gold  },
    { id: 'claude', label: 'CLAUDE', desc: 'Force Claude only',            color: THEME.colors.blue  },
    { id: 'groq',   label: 'GROQ',   desc: 'Force Groq only (free)',       color: THEME.colors.green },
    { id: 'off',    label: 'OFF',    desc: 'Disable AI completely',        color: THEME.colors.red   },
  ];

  return (
    <>
      {/* Status card */}
      <div style={{ ...S.card('rgba(0,212,255,0.06)'), padding: '12px 14px' }}>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px', letterSpacing: '1px' }}>ACTIVE AI ENGINE</div>
        <div style={{ fontSize: '15px', fontWeight: '700', color: engineColor }}>{engineLabel}</div>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '4px' }}>
          {engine?.hasClaude ? 'Claude Key: Set' : 'Claude Key: Not Set'}&nbsp;&nbsp;|&nbsp;&nbsp;
          {engine?.hasGroq   ? 'Groq Key: Set'   : 'Groq Key: Not Set'}
        </div>
      </div>

      {/* Manual Provider Toggle */}
      <div style={S.card()}>
        <div style={{ ...S.section, marginBottom: '8px' }}>AI PROVIDER — MANUAL CONTROL</div>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '10px' }}>
          तुम्ही manually select करा — कोणती AI वापरायची
        </div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
          {prefOptions.map(opt => {
            const isActive = pref === opt.id;
            return (
              <button
                key={opt.id}
                disabled={prefSaving}
                onClick={() => setPreference(opt.id)}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  borderRadius: '6px',
                  border: `2px solid ${isActive ? opt.color : THEME.colors.border}`,
                  background: isActive ? `rgba(${opt.color === THEME.colors.blue ? '0,212,255' : opt.color === THEME.colors.green ? '0,255,136' : opt.color === THEME.colors.red ? '255,51,51' : '255,215,0'},0.15)` : 'rgba(255,255,255,0.03)',
                  color: isActive ? opt.color : THEME.colors.textMuted,
                  fontSize: '11px',
                  fontWeight: '700',
                  fontFamily: 'monospace',
                  cursor: prefSaving ? 'wait' : 'pointer',
                  letterSpacing: '1px',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>
          {prefOptions.find(o => o.id === pref)?.desc || ''}
          {prefSaving ? ' — Saving...' : ''}
        </div>
      </div>

      <div style={S.card()}>
        <div style={{ ...S.section, color: THEME.colors.blue }}>CLAUDE AI KEY — PRIMARY (BEST)</div>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '10px', lineHeight: '1.7' }}>
          200K context window &bull; Real chart vision &bull; No token limit errors<br />
          Get key: <strong style={{ color: THEME.colors.blue }}>console.anthropic.com</strong> &rarr; API Keys<br />
          Key format: <strong>sk-ant-api03-...</strong>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            style={{ ...S.input, flex: 1 }}
            type="password"
            placeholder="sk-ant-api03-..."
            value={claudeKey}
            onChange={e => setClaudeKey(e.target.value)}
          />
          <button
            style={{ ...S.btn('primary'), opacity: (loading || !claudeKey.startsWith('sk-ant-')) ? 0.5 : 1 }}
            disabled={loading || !claudeKey.startsWith('sk-ant-')}
            onClick={() => saveKey('claude', claudeKey)}
          >
            Save
          </button>
        </div>
        {engine?.hasClaude && (
          <div style={{ fontSize: '11px', color: THEME.colors.green, marginTop: '6px' }}>✅ Claude key already active — enter new key to update</div>
        )}
      </div>

      <div style={S.card()}>
        <div style={{ ...S.section, color: THEME.colors.green }}>GROQ KEY — FALLBACK (FREE)</div>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '10px', lineHeight: '1.7' }}>
          Free forever &bull; Used if Claude key not set &bull; Lower token limits<br />
          Get key: <strong style={{ color: THEME.colors.green }}>console.groq.com</strong> &rarr; API Keys<br />
          Key format: <strong>gsk_...</strong>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            style={{ ...S.input, flex: 1 }}
            type="password"
            placeholder="gsk_..."
            value={groqKey}
            onChange={e => setGroqKey(e.target.value)}
          />
          <button
            style={{ ...S.btn('green'), opacity: (loading || !groqKey.startsWith('gsk_')) ? 0.5 : 1 }}
            disabled={loading || !groqKey.startsWith('gsk_')}
            onClick={() => saveKey('groq', groqKey)}
          >
            Save
          </button>
        </div>
        {engine?.hasGroq && (
          <div style={{ fontSize: '11px', color: THEME.colors.green, marginTop: '6px' }}>✅ Groq key set (fallback ready)</div>
        )}
      </div>

      {status && (
        <div style={{ ...S.card(status.ok ? 'rgba(0,255,136,0.08)' : 'rgba(255,51,51,0.08)'), padding: '10px 14px' }}>
          <div style={{ fontSize: '12px', color: status.ok ? THEME.colors.green : THEME.colors.red }}>
            {status.ok ? '✅' : '❌'} {status.msg}
          </div>
        </div>
      )}

      <div style={{ ...S.card('rgba(255,215,0,0.04)'), padding: '10px 14px' }}>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, lineHeight: '1.7' }}>
          Keys config.json मध्ये securely store होतात (userData folder).<br />
          JARVIS restart नंतर new key active होतो.
        </div>
      </div>
    </>
  );
}

// ─── About Tab ────────────────────────────────────────────────────────────────
function AboutTab() {
  const phases = [
    { n: '0-4',  label: 'Foundation: Storage, AI, Brain, Context', done: true },
    { n: '5-6',  label: 'UI: Boot, App Shell, Chat',               done: true },
    { n: '7-8',  label: 'Teaching: Daily Recap, Dashboard',        done: true },
    { n: '9-10', label: 'Knowledge: KB, Rule Book',                done: true },
    { n: '11',   label: 'ICT/SMC Module',                          done: true },
    { n: '12',   label: 'Fibonacci Calculator (23 levels)',        done: true },
    { n: '13',   label: 'Confluence Engine',                       done: true },
    { n: '14',   label: 'Trade Journal',                           done: true },
    { n: '15',   label: 'Risk Manager',                            done: true },
    { n: '16',   label: 'Stats & Performance',                     done: true },
    { n: '17',   label: 'JARVIS Memory + Self-Evolution',          done: true },
    { n: '18',   label: 'Psychology Tracker',                      done: true },
    { n: '19',   label: 'PWA + Notifications + Settings',         done: true },
    { n: '20',   label: 'Multi-Agent System',                      done: false },
  ];

  return (
    <>
      <div style={{ ...S.card('rgba(255,215,0,0.06)'), padding: '14px' }}>
        <div style={S.section}>JARVIS 3.0</div>
        <div style={{ fontSize: '13px', color: THEME.colors.textMuted, lineHeight: '1.8' }}>
          <div>Version: <strong style={{ color: THEME.colors.gold }}>3.0.0</strong></div>
          <div>AI Engine: <strong style={{ color: THEME.colors.blue }}>Claude AI / Groq (dual mode)</strong></div>
          <div>Price Feed: <strong style={{ color: THEME.colors.green }}>Twelve Data API</strong></div>
          <div>Storage: <strong style={{ color: THEME.colors.purple }}>LocalStorage (AES Encrypted)</strong></div>
          <div>Built for: <strong style={{ color: THEME.colors.gold }}>XAU/USD Gold Trading</strong></div>
        </div>
      </div>

      <div style={S.card()}>
        <div style={S.section}>BUILD PROGRESS</div>
        {phases.map(p => (
          <div key={p.n} style={{ display: 'flex', gap: '10px', fontSize: '12px', marginBottom: '5px', alignItems: 'center' }}>
            <span style={{ color: p.done ? THEME.colors.green : THEME.colors.textMuted, width: '12px', flexShrink: 0 }}>{p.done ? '[+]' : '[ ]'}</span>
            <span style={{ color: THEME.colors.textMuted, width: '28px', flexShrink: 0 }}>P{p.n}</span>
            <span style={{ color: p.done ? THEME.colors.text : THEME.colors.textMuted }}>{p.label}</span>
          </div>
        ))}
      </div>

      <div style={{ ...S.card('rgba(0,212,255,0.04)'), padding: '12px 14px' }}>
        <div style={S.section}>API SETUP</div>
        <div style={{ fontSize: '12px', color: THEME.colors.textMuted, lineHeight: '1.8' }}>
          <div>Claude AI: <strong style={{ color: THEME.colors.blue }}>console.anthropic.com</strong> (Primary — sk-ant- key)</div>
          <div>Groq Free: <strong style={{ color: THEME.colors.green }}>console.groq.com</strong> (Fallback — gsk_ key)</div>
          <div>Keys Settings &rarr; AI Keys tab मध्ये add करा</div>
          <div>Twelve Data: <strong>twelvedata.com</strong> (Price feed)</div>
        </div>
      </div>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SettingsScreen() {
  const [tab, setTab] = useState('aikeys');

  const tabs = [
    { id: 'aikeys',        label: 'AI Keys'     },
    { id: 'notifications', label: 'Alerts'      },
    { id: 'price',         label: 'Price'       },
    { id: 'data',          label: 'Data'        },
    { id: 'about',         label: 'About'       },
  ];

  return (
    <div style={S.page}>
      <div style={S.title}>SETTINGS</div>
      <div style={S.sub}>AI KEYS · NOTIFICATIONS · PRICE ALERTS · DATA · ABOUT</div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
        {tabs.map(t => (
          <button key={t.id} style={{ ...S.tabBtn(tab === t.id), padding: '7px 4px' }}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'aikeys'         && <AIKeysTab        />}
      {tab === 'notifications' && <NotificationsTab />}
      {tab === 'price'         && <PriceAlertsTab   />}
      {tab === 'data'          && <DataTab           />}
      {tab === 'about'         && <AboutTab          />}
    </div>
  );
}



